/**
 * ज्ञानकिरण from the admin side: the cards collection, their pictures, and
 * the two things only a function may do (publish, push).
 *
 * The app never reads `dnyankiran/`; it reads `content/dnyankiranToday`,
 * which only the publish function writes. So nothing here can put a card in
 * front of a reader by accident -- Schedule and Publish now both go through
 * the function.
 */
import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc, query, orderBy,
  serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import {
  COLLECTIONS, STORAGE_PATHS, CONTENT_PUBLIC_URL, DNYANKIRAN_SETTINGS_DOC, DEFAULT_DNYANKIRAN_TIME,
} from '@dnyaneshwari/shared';
import type { DnyankiranCard, DnyankiranSettings, Ovi } from '@dnyaneshwari/shared';
import { db, storage, functions } from './firebase';

const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;

function toDate(v: unknown): Date | null {
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return null;
}

function fromDoc(id: string, d: Record<string, unknown>): DnyankiranCard {
  return {
    id,
    number: Number(d.number ?? 0),
    category: String(d.category ?? ''),
    title: (d.title as DnyankiranCard['title']) ?? { mr: '' },
    ovi: (d.ovi as DnyankiranCard['ovi']) ?? { chapterId: '01', oviNumber: 1, textMarathi: '' },
    oviEnglish: typeof d.oviEnglish === 'string' ? d.oviEnglish : undefined,
    explanation: (d.explanation as DnyankiranCard['explanation']) ?? { mr: '' },
    teaching: (d.teaching as DnyankiranCard['teaching']) ?? { mr: '' },
    illustrationUrl: typeof d.illustrationUrl === 'string' ? d.illustrationUrl : undefined,
    shareImageUrl: (d.shareImageUrl as DnyankiranCard['shareImageUrl']) ?? undefined,
    videoUrl: (d.videoUrl as DnyankiranCard['videoUrl']) ?? undefined,
    status: (d.status as DnyankiranCard['status']) ?? 'draft',
    publishAt: toDate(d.publishAt),
    publishedAt: toDate(d.publishedAt),
    pushSent: d.pushSent === true,
    createdAt: toDate(d.createdAt) ?? new Date(0),
    updatedAt: toDate(d.updatedAt) ?? new Date(0),
  };
}

/** Every card, newest number first. */
export async function listCards(): Promise<DnyankiranCard[]> {
  const snap = await getDocs(query(collection(db, COLLECTIONS.DNYANKIRAN), orderBy('number', 'desc')));
  return snap.docs.map((s) => fromDoc(s.id, s.data()));
}

export async function getCard(id: string): Promise<DnyankiranCard | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.DNYANKIRAN, id));
  return snap.exists() ? fromDoc(snap.id, snap.data()) : null;
}

/** The next क्र.: one more than the highest number in use. */
export async function nextNumber(): Promise<number> {
  const cards = await listCards();
  return cards.reduce((m, c) => Math.max(m, c.number), 0) + 1;
}

export type CardDraft = Omit<DnyankiranCard, 'id' | 'status' | 'publishAt' | 'publishedAt' | 'pushSent' | 'createdAt' | 'updatedAt'>;

/**
 * Creates or updates a card's content. Never touches status, the schedule or
 * the push flag -- those move only through schedule/unschedule and the
 * function -- so editing a published card's typo does not re-publish or
 * re-push it. (The live copy updates on the next Publish now.)
 */
export async function saveCard(id: string | null, draft: CardDraft): Promise<string> {
  const clean = JSON.parse(JSON.stringify(draft)) as CardDraft; // drops undefined, which Firestore rejects
  if (id) {
    await updateDoc(doc(db, COLLECTIONS.DNYANKIRAN, id), { ...clean, updatedAt: serverTimestamp() });
    return id;
  }
  const newRef = doc(collection(db, COLLECTIONS.DNYANKIRAN));
  await setDoc(newRef, {
    ...clean,
    status: 'draft',
    publishAt: null,
    publishedAt: null,
    pushSent: false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return newRef.id;
}

/** Sets when the function should publish the card, and whether to notify. */
export async function scheduleCard(id: string, publishAt: Date, sendPush: boolean): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.DNYANKIRAN, id), {
    status: 'scheduled',
    publishAt: Timestamp.fromDate(publishAt),
    sendPush,
    updatedAt: serverTimestamp(),
  });
}

/** Back to draft. Does not take a published card off the app -- publishing another one does that. */
export async function unscheduleCard(id: string): Promise<void> {
  await updateDoc(doc(db, COLLECTIONS.DNYANKIRAN, id), {
    status: 'draft',
    publishAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCard(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTIONS.DNYANKIRAN, id));
}

/** Publish now, or push a published card again. Both run in the function. */
export async function publishNow(cardId: string, resend = false, push = true): Promise<{ pushed: boolean }> {
  const callable = httpsCallable<{ cardId: string; resend: boolean; push: boolean }, { success: boolean; pushed: boolean }>(
    functions,
    'publishDnyankiranNow'
  );
  const res = await callable({ cardId, resend, push });
  return { pushed: res.data.pushed };
}

export type AssetKind = 'illustration' | 'share-mr' | 'share-en' | 'video-mr' | 'video-en';

/** Uploads one of a card's files to images/dnyankiran/{cardId}/ and returns its public URL. */
export function uploadAsset(cardId: string, kind: AssetKind, file: File, onProgress: (pct: number) => void): Promise<string> {
  const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
  const storageRef = ref(storage, `${STORAGE_PATHS.DNYANKIRAN}/${cardId}/${kind}.${ext}`);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file, { contentType: file.type || undefined });
    task.on(
      'state_changed',
      (snap) => onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      reject,
      async () => resolve(await getDownloadURL(task.snapshot.ref))
    );
  });
}

/** The verse from the published bundle, so the card carries the book's text. */
export async function fetchOviText(chapterId: string, oviNumber: number): Promise<Ovi | null> {
  const padded = chapterId.padStart(2, '0');
  const id = `${padded}-${String(oviNumber).padStart(3, '0')}`;
  const res = await fetch(`${CONTENT_BASE}/chapters/${padded}.json`);
  if (!res.ok) return null;
  const file = (await res.json()) as { ovis: Ovi[] };
  return file.ovis.find((o) => o.id === id) ?? null;
}

export async function getSettings(): Promise<DnyankiranSettings> {
  const snap = await getDoc(doc(db, COLLECTIONS.SETTINGS, DNYANKIRAN_SETTINGS_DOC));
  const sendTime = snap.exists() ? (snap.data().sendTime as string | undefined) : undefined;
  return { sendTime: sendTime || DEFAULT_DNYANKIRAN_TIME };
}

export async function setSettings(settings: DnyankiranSettings): Promise<void> {
  await setDoc(doc(db, COLLECTIONS.SETTINGS, DNYANKIRAN_SETTINGS_DOC), settings, { merge: true });
}
